import { BsdsCard } from '@bsds-lib-component/react';
import React, { useEffect, useMemo, useState } from 'react';

import { SubEstipulantesTable } from '../..';
import { formatDocSubestipulante, formatVigenciaSubDisplay } from '../../../hooks';
import type { UseSubEstipulanteResumoReturn } from '../../../hooks/vida/useSubEstipulanteInfo';
import type { ListaSubestipulanteItem } from '../../../types/vida/subEstipulante/subEstipulanteType';
import { formatSubestipulanteCodNome } from '../../../utils/subestipulanteUtil';

export type SubestipulantesViewAction = 'segurados' | 'proponentes';

const SUBESTIPULANTES_PAGE_SIZE = 6;

type SubestipulantesListSectionProps = {
  hasSearched: boolean;
  selectedSubestipulante: ListaSubestipulanteItem | null;
  subestipulantesList: ListaSubestipulanteItem[];
  numeroApolice?: string;
  viewAction: SubestipulantesViewAction;
  subestipulanteResumo?: UseSubEstipulanteResumoReturn;
  onViewSubestipulante: (row: ListaSubestipulanteItem) => void;
  onInfoSubEstipulante: (row: ListaSubestipulanteItem) => void;
};

const SubestipulantesListSection: React.FC<SubestipulantesListSectionProps> = ({
  hasSearched,
  selectedSubestipulante,
  subestipulantesList,
  numeroApolice,
  viewAction,
  subestipulanteResumo,
  onViewSubestipulante,
  onInfoSubEstipulante,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const tableViewProps = viewAction === 'segurados'
    ? { onViewSegurados: onViewSubestipulante }
    : { onViewProponentes: onViewSubestipulante };

  const subestipulanteLabel = formatSubestipulanteCodNome(
    selectedSubestipulante?.codSubestipulanteOut,
    selectedSubestipulante?.nomeSubestipulanteOut,
  );

  const totalItems = subestipulantesList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / SUBESTIPULANTES_PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [subestipulantesList]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedItems = useMemo(() => {
    const page = Math.min(currentPage, totalPages);
    const start = (page - 1) * SUBESTIPULANTES_PAGE_SIZE;
    return subestipulantesList.slice(start, start + SUBESTIPULANTES_PAGE_SIZE);
  }, [currentPage, subestipulantesList, totalPages]);

  return (
    <div className="bsds-mb-32">
      {hasSearched && (
        <BsdsCard
          className="grid grid-cols-1 md:grid-cols-4 gap-4 bsds-p-16 bg-gray-100/25 bsds-mb-16 shadow-none rounded-sm bsds-mb-20"
          platform="web"
          role="region"
          aria-label="Dados do Sub-Estipulante selecionado"
        >
          <div>
            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Sub-Estipulante</span>
            <span
              className="bsds-text-regular font-semibold"
              aria-label={`Sub-Estipulante: ${subestipulanteLabel}`}
            >
              {subestipulanteLabel}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">ID</span>
            <span
              className="bsds-text-regular font-semibold"
              aria-label={`ID do Sub-Estipulante: ${selectedSubestipulante?.identSubestipulanteOut ?? '—'}`}
            >
              {selectedSubestipulante?.identSubestipulanteOut ?? '—'}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Número do Documento</span>
            <span
              className="bsds-text-regular font-semibold"
              aria-label={`Documento do Sub-Estipulante: ${formatDocSubestipulante(selectedSubestipulante?.numDocumentoOut)}`}
            >
              {formatDocSubestipulante(selectedSubestipulante?.numDocumentoOut)}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Status</span>
            <span
              className="bsds-text-regular font-semibold"
              aria-label={`Status do Sub-Estipulante: ${selectedSubestipulante?.dsCstatus?.trim() || '—'}`}
            >
              {selectedSubestipulante?.dsCstatus?.trim() || '—'}
            </span>
          </div>
        </BsdsCard>
      )}

      {!hasSearched && (
        <BsdsCard className="bsds-p-16 bsds-mb-16">
          <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
            <h5 className="opacity-80 bsds-mb-0">Sub-Estipulantes</h5>
          </div>

          <SubEstipulantesTable
            items={paginatedItems}
            formatVigencia={formatVigenciaSubDisplay}
            formatDocumento={formatDocSubestipulante}
            numeroApolice={numeroApolice}
            subestipulanteResumo={subestipulanteResumo}
            onInfoSubEstipulante={onInfoSubEstipulante}
            emptyMessage="Nenhum subestipulante disponível para a apólice selecionada"
            currentPage={Math.min(currentPage, totalPages)}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            {...tableViewProps}
          />
        </BsdsCard>
      )}
    </div>
  );
};

export default SubestipulantesListSection;
